/**
 * Yumz share-cards v2 — unified status table.
 *
 * Single source of truth for the 7-status visual + copy system used by both
 * Flow A (check-in / place vibe) and Flow B (dish verdict) renderers.
 *
 * The legacy MOOD_TAGS (8 occasion keys) and ratingConfig (3-rating verdict
 * system) in components/ShareCards.tsx are consolidated into these 7 statuses.
 * See mapMoodToStatus / mapRatingToStatus below.
 */

export type ShareStatus =
  | "fire"
  | "vibes"
  | "honest"
  | "mid"
  | "elite"
  | "cozy"
  | "lowkey";

export interface StatusDefinition {
  key: ShareStatus;
  label: string;        // lowercase display label, e.g. "fire"
  emoji: string;        // status emoji
  palette: {
    bg1: string;        // primary gradient stop
    bg2: string;        // secondary gradient stop
    accent: string;     // accent / glow color
    accentText: string; // text color on accent fills
    text: string;       // default text color on this card
    subText: string;    // muted text color on this card
  };
  // Atmospheric texture key — drives renderer-specific bg treatment
  // (neon = diagonal energy lines, glow = radial blobs, paper = receipt grid,
  // deadpan = dot grid, parchment = candle-lit black + gold,
  // candle = caramel + flame glow, midnight = deep blue + secondary glow)
  texture: "neon" | "glow" | "paper" | "deadpan" | "parchment" | "candle" | "midnight";
  pill: {
    bg: string;
    color: string;
    border: string | null;
  };
  copy: {
    // Flow A — "I'm here now"
    checkInHeaders: string[];
    checkInLines: string[];
    // Flow B — "I ate this"
    verdictHeaders: string[];
    verdictLines: string[];
  };
}

export const SHARE_STATUSES: Record<ShareStatus, StatusDefinition> = {
  fire: {
    key: "fire",
    label: "fire",
    emoji: "🔥",
    palette: {
      bg1: "#2a0500",
      bg2: "#f04500",
      accent: "#ff3a00",
      accentText: "#ffffff",
      text: "#ffffff",
      subText: "rgba(255,255,255,0.78)",
    },
    texture: "neon",
    pill: { bg: "#ff3a00", color: "#ffffff", border: "rgba(255,255,255,0.5)" },
    copy: {
      checkInHeaders: ["currently at", "tonight is the move", "we pulled up to"],
      checkInLines: [
        "this place is lit and we're staying till close",
        "the energy is unmatched rn. ordering everything",
        "walked in and the vibe hit immediately",
        "no thoughts, just pure fire. the menu doesn't stand a chance",
      ],
      verdictHeaders: ["verdict", "this dish", "the take"],
      verdictLines: [
        "actually gasped. this just entered the hall of fame",
        "10/10 no notes. the chef understood the assignment",
        "obsessed is an understatement. coming back for this alone",
        "the flavors said 'you're welcome' and honestly? thank you",
      ],
    },
  },

  vibes: {
    key: "vibes",
    label: "vibes",
    emoji: "✨",
    palette: {
      bg1: "#0c0617",
      bg2: "#1a0c2e",
      accent: "#a855f7",
      accentText: "#ffffff",
      text: "#ffffff",
      subText: "rgba(255,255,255,0.78)",
    },
    texture: "glow",
    pill: { bg: "#a855f7", color: "#ffffff", border: "#f0abfc" },
    copy: {
      checkInHeaders: ["tonight's move", "currently giving", "the vibe is"],
      checkInLines: [
        "the ambience is right. golden-hour glow, perfect playlist",
        "good food good mood. main character energy activated",
        "the energy in here matches the moment perfectly",
        "vibes immaculate. ordering everything that fits the mood",
      ],
      verdictHeaders: ["matched the moment", "this dish", "fit the mood"],
      verdictLines: [
        "dish fit the mood. soft music, soft food, soft heart",
        "this is exactly what the moment needed",
        "the flavors matched the vibe and now i'm in my feelings",
        "fit the night so perfectly i forgot to take a second photo",
      ],
    },
  },

  honest: {
    key: "honest",
    label: "honest",
    emoji: "💯",
    palette: {
      bg1: "#f1ebe1",
      bg2: "#ddd2b9",
      accent: "#d23300",
      accentText: "#ffffff",
      text: "#1a1207",
      subText: "#4a3520",
    },
    texture: "paper",
    pill: { bg: "#1a1207", color: "#ffffff", border: "#1a1207" },
    copy: {
      checkInHeaders: ["first impression", "real talk", "live take"],
      checkInLines: [
        "no filter — interior is loud, menu is interesting, food TBD",
        "real talk: this place is solid. nothing world-ending yet",
        "honest review pending. so far the bread is winning",
        "walked in with low expectations. so far so good",
      ],
      verdictHeaders: ["real review", "honest take", "the verdict"],
      verdictLines: [
        "real talk: 8/10. they understood the assignment",
        "honest review: it's good. not life-changing but solid",
        "no filter. the dish delivered exactly what was promised",
        "would order again. would also split with someone",
      ],
    },
  },

  mid: {
    key: "mid",
    label: "mid",
    emoji: "😐",
    palette: {
      bg1: "#2c2c2a",
      bg2: "#1a1a18",
      accent: "#5f5e5a",
      accentText: "#ffffff",
      text: "#d6d3c8",
      subText: "rgba(214,211,200,0.7)",
    },
    texture: "deadpan",
    pill: { bg: "#5f5e5a", color: "#ffffff", border: "#888780" },
    copy: {
      checkInHeaders: ["posting from", "currently at", "we're at"],
      checkInLines: [
        "it's fine. service is okay, music is forgettable",
        "place is mid ngl. the people-watching is solid though",
        "not great, not terrible. it exists and we're here",
        "the vibes are room temperature. we'll see what the food does",
      ],
      verdictHeaders: ["verdict", "the take", "this dish"],
      verdictLines: [
        "solid 5/10. wouldn't fight for the last bite",
        "the description was a work of fiction apparently",
        "mid is mid. no drama, no excitement, just food",
        "it's giving lunch meeting energy. forgettable",
      ],
    },
  },

  elite: {
    key: "elite",
    label: "elite",
    emoji: "👑",
    palette: {
      bg1: "#0a0805",
      bg2: "#2a1f0a",
      accent: "#d4af37",
      accentText: "#1a1207",
      text: "#f6e4b5",
      subText: "rgba(246,228,181,0.78)",
    },
    texture: "parchment",
    pill: { bg: "#d4af37", color: "#1a1207", border: "#f6d97a" },
    copy: {
      checkInHeaders: ["top tier spot", "iykyk", "currently"],
      checkInLines: [
        "if you know you know. white-tablecloth energy, this is THE move",
        "premium spot. sommelier knows our name",
        "the room is doing too much in the best way",
        "top tier no further notes. we made it",
      ],
      verdictHeaders: ["iykyk", "verdict", "top tier"],
      verdictLines: [
        "top tier no further notes. chef exceeded the assignment",
        "this dish just entered the elite tier 👑",
        "perfection. came correct. came RIGHT",
        "if you know you know. ordering this every visit forever",
      ],
    },
  },

  cozy: {
    key: "cozy",
    label: "cozy",
    emoji: "🕯️",
    palette: {
      bg1: "#1a0a02",
      bg2: "#75421a",
      accent: "#c58a4a",
      accentText: "#1a0a02",
      text: "#ffe4c0",
      subText: "rgba(255,228,192,0.78)",
    },
    texture: "candle",
    pill: { bg: "#c58a4a", color: "#1a0a02", border: "#ffd9a8" },
    copy: {
      checkInHeaders: ["tucked in at", "tonight's spot", "the cozy move"],
      checkInLines: [
        "warm hug energy. corner booth, candle is lit",
        "comfort food incoming. we're not leaving",
        "everything about this whispers 'stay a while'",
        "the ambiance is candlelit and the heart is full",
      ],
      verdictHeaders: ["comfort food", "verdict", "warm take"],
      verdictLines: [
        "comfort food in poster form. soft, warm, exactly right",
        "tasted like a hug. would order on every cold day",
        "the kind of dish that makes you slow down and stay",
        "warm, generous, made with love. you can taste it",
      ],
    },
  },

  lowkey: {
    key: "lowkey",
    label: "lowkey",
    emoji: "😎",
    palette: {
      bg1: "#04101a",
      bg2: "#0a1f33",
      accent: "#1b3d2f",
      accentText: "#a8e6c8",
      text: "#d6e2f0",
      subText: "rgba(214,226,240,0.78)",
    },
    texture: "midnight",
    pill: { bg: "#1b3d2f", color: "#a8e6c8", border: "#4d8a6f" },
    copy: {
      checkInHeaders: ["secret spot", "the hidden gem", "quietly"],
      checkInLines: [
        "hidden gem energy. nobody's instagramming this yet",
        "lowkey the move. don't tell anyone",
        "quiet spot, loud flavors. our little secret",
        "the kind of place you keep close to your chest",
      ],
      verdictHeaders: ["quietly excellent", "verdict", "the secret"],
      verdictLines: [
        "lowkey kinda slaps. earned a spot in the rotation",
        "quietly excellent. not screaming about it but it hits",
        "the unsung hero of the menu. zero hype, all delivery",
        "this might be underrated and that's exactly why it hits",
      ],
    },
  },
};

/** Pick a random copy line for the given status + flow. */
export function pickStatusCopy(
  status: ShareStatus,
  field: keyof StatusDefinition["copy"],
): string {
  const def = SHARE_STATUSES[status];
  const arr = def.copy[field];
  if (!arr.length) return "";
  return arr[Math.floor(Math.random() * arr.length)];
}

// ─── legacy → v2 mappers ────────────────────────────────────────────
// These let v1 callers (MOOD_TAGS in ShareCards.tsx, rating in VerdictShareCard,
// ShareTone in shareTargets.ts) delegate to v2 renderers via the feature flag
// without changing their call sites.

/**
 * MOOD_TAGS (8 occasion keys: fire / cozy / bougie / date-night / squad /
 * solo-treat / family / brunch) → ShareStatus.
 *
 * Per Robin's policy: the 8 occasion keys collapse to the 7 visual statuses.
 * Occasion semantics that don't have a direct visual analog (date-night,
 * brunch) map to "vibes" — they're moment-driven, not verdict-driven.
 */
export function mapMoodToStatus(mood: string | null | undefined): ShareStatus {
  switch (mood) {
    case "fire": return "fire";
    case "cozy": return "cozy";
    case "bougie": return "elite";
    case "date-night": return "vibes";
    case "squad": return "vibes";
    case "solo-treat": return "lowkey";
    case "family": return "cozy";
    case "brunch": return "vibes";
    default: return "vibes"; // safe default for unknown / null
  }
}

/**
 * 3-rating verdict (slaps / mid / skip) → ShareStatus.
 * slaps → fire (positive enthusiasm), mid → mid (one-to-one),
 * skip → honest (real take, accommodates negative reviews without a "trash" tier).
 */
export function mapRatingToStatus(rating: "slaps" | "mid" | "skip"): ShareStatus {
  switch (rating) {
    case "slaps": return "fire";
    case "mid":   return "mid";
    case "skip":  return "honest";
  }
}

/**
 * ShareTone (existing 7-tone system in shareTargets.ts) → ShareStatus.
 * One-to-one: fire/honest/mid/elite/cozy/lowkey are identical;
 * "vibe" (singular) becomes "vibes" (plural — matches the new label).
 */
export function mapToneToStatus(tone: string | null | undefined): ShareStatus {
  switch (tone) {
    case "fire":   return "fire";
    case "vibe":   return "vibes";
    case "vibes":  return "vibes";
    case "honest": return "honest";
    case "mid":    return "mid";
    case "elite":  return "elite";
    case "cozy":   return "cozy";
    case "lowkey": return "lowkey";
    default: return "vibes";
  }
}
